import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
} from '@fluentui/react-components';
import { ChevronDown16Regular, Dismiss16Regular, DocumentText16Regular, Search20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideCountChip } from '../../../theme/components/GuideKpiCard';
import { GuideTooltip } from '../../../theme/components/GuideTooltip';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideBannerAction } from '../../../theme/components/GuideButtons';
import { StatusPill } from '../../../theme/components/StatusPill';
import { formatImportAmount } from '../../../functions/ProviderConsumptionImport.Functions';
import { importedAtLabel, itemErrorRows, ItemServiceOverride, voucherLabel } from '../../../functions/ProviderConsumptionImport.Review.Functions';
import {
  IMPORT_DUPLICATE_STATUSES,
  ImportAssignment,
  ImportDuplicateStatus,
  ImportItemError,
  ImportPreviewSummary,
  VoucherAlreadyImported,
  VoucherHeader,
} from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { ImportAssignmentFields } from './ImportAssignmentFields';
import { AssignmentOptions } from './useAssignmentOptions';

/**
 * Barra de "Revisar ítems" (spec 009): encabezado de selección (casilla + menú "Página" / "Todos los filtrados" /
 * "Ninguno"), resumen en vivo (detectados, seleccionados, importe seleccionado), chips de filtro por estado con
 * contador, búsqueda con debounce, chip del comprobante y aviso de comprobante ya importado.
 */

export interface ImportSelectionHeaderProps {
  /** Estado de la casilla para la página visible. */
  checked: boolean | 'mixed';
  /** La página no tiene ítems seleccionables. */
  disabled?: boolean;
  /** "Todos los filtrados" en curso. */
  busy?: boolean;
  onTogglePage: () => void;
  onSelectPage: () => void;
  onSelectAllFiltered: () => void;
  onSelectNone: () => void;
}

/** Casilla del encabezado (marca o desmarca la página) y menú con las tres formas de seleccionar. */
export const ImportSelectionHeader = ({ checked, disabled, busy, onTogglePage, onSelectPage, onSelectAllFiltered, onSelectNone }: ImportSelectionHeaderProps) => {
  const { t } = useTranslation();
  const tr = (key: string) => t(`providerConsumptionImport.review.${key}`) as string;
  return (
    <span className="pci-review-check-header">
      <Checkbox
        checked={checked}
        disabled={disabled || busy}
        onChange={onTogglePage}
        aria-label={tr('selectPageLabel')}
        data-testid="pci-review-check-page"
      />
      <Menu positioning="below-start">
        <MenuTrigger disableButtonEnhancement>
          <GuideTooltip content={tr('selectionMenu')} relationship="label">
            <Button
              appearance="subtle"
              size="small"
              className="pci-review-check-menu"
              icon={busy ? <Spinner size="extra-tiny" /> : <ChevronDown16Regular />}
              disabled={busy}
              aria-busy={busy || undefined}
              data-testid="pci-review-selection-menu"
            />
          </GuideTooltip>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem onClick={onSelectPage} disabled={disabled} data-testid="pci-review-select-page">
              {tr('selectPage')}
            </MenuItem>
            <MenuItem onClick={onSelectAllFiltered} data-testid="pci-review-select-all">
              {tr('selectAllFiltered')}
            </MenuItem>
            <MenuItem onClick={onSelectNone} data-testid="pci-review-select-none">
              {tr('selectNone')}
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    </span>
  );
};

/** Debounce de la búsqueda (FR-002). */
export const REVIEW_SEARCH_DEBOUNCE_MS = 300;

const STATUS_COUNT_KEYS: Record<ImportDuplicateStatus, keyof ImportPreviewSummary> = {
  [ImportDuplicateStatus.New]: 'new',
  [ImportDuplicateStatus.CopyOfVoucher]: 'copyOfVoucher',
  [ImportDuplicateStatus.PossibleDuplicate]: 'possibleDuplicate',
  [ImportDuplicateStatus.AlreadyImportedThisFile]: 'alreadyImportedThisFile',
  [ImportDuplicateStatus.AlreadyImportedOtherFile]: 'alreadyImportedOtherFile',
};

export interface ImportReviewFiltersProps {
  summary?: ImportPreviewSummary | null;
  statuses: ImportDuplicateStatus[];
  search: string;
  onStatusesChange: (statuses: ImportDuplicateStatus[]) => void;
  /** Se llama 300 ms después de la última tecla (la tabla vuelve a la página 1). */
  onSearchChange: (search: string) => void;
  disabled?: boolean;
}

/**
 * Chips de filtro por estado (botones conmutables con `aria-pressed` y el contador de `summary`; varios a la vez) y
 * búsqueda con debounce de 300 ms. "Todos" quita el filtro de estado.
 */
export const ImportReviewFilters = ({ summary, statuses, search, onStatusesChange, onSearchChange, disabled }: ImportReviewFiltersProps) => {
  const { t } = useTranslation();
  const tr = (key: string) => t(`providerConsumptionImport.review.filters.${key}`) as string;
  const [text, setText] = useState(search);
  const timer = useRef<number | undefined>(undefined);

  // Un cambio externo (vista previa nueva) reemplaza el texto.
  useEffect(() => setText(search), [search]);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const onType = (value: string) => {
    setText(value);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => onSearchChange(value.trim()), REVIEW_SEARCH_DEBOUNCE_MS);
  };

  const toggle = (status: ImportDuplicateStatus) =>
    onStatusesChange(statuses.includes(status) ? statuses.filter((value) => value !== status) : [...statuses, status].sort((a, b) => a - b));

  return (
    <div className="pci-review-filters">
      <fieldset className="pci-review-chips" data-testid="pci-review-chips">
        <legend className="pci-sr">{tr('label')}</legend>
        <button
          type="button"
          className="pci-review-chip"
          aria-pressed={statuses.length === 0}
          disabled={disabled}
          onClick={() => onStatusesChange([])}
          data-testid="pci-review-chip-all"
        >
          {tr('all')}
          <b className="pci-review-chip__count">{summary?.total ?? 0}</b>
        </button>
        {IMPORT_DUPLICATE_STATUSES.map((status) => (
          <button
            key={status}
            type="button"
            className="pci-review-chip"
            aria-pressed={statuses.includes(status)}
            disabled={disabled}
            onClick={() => toggle(status)}
            data-testid={`pci-review-chip-${status}`}
          >
            {tr(`kinds.${status}`)}
            <b className="pci-review-chip__count">{Number(summary?.[STATUS_COUNT_KEYS[status]] ?? 0)}</b>
          </button>
        ))}
      </fieldset>
      <Input
        type="search"
        className="pci-review-search"
        contentBefore={<Search20Regular />}
        value={text}
        maxLength={200}
        onChange={(_event, data) => onType(data.value)}
        aria-label={tr('search')}
        placeholder={tr('searchHelp')}
        data-testid="pci-review-search"
      />
    </div>
  );
};

export interface ImportVoucherInfoProps {
  voucher?: VoucherHeader | null;
  voucherAlreadyImported?: VoucherAlreadyImported | null;
  /** "Ver archivo": abre `blobPath` en la vista previa de archivos. */
  onViewFile?: (path: string, name: string) => void;
}

/**
 * Chip del comprobante ("Factura A 00003-00000509 · WILKINS GONZALO MATIAS · 3 copias") y aviso de comprobante ya
 * importado desde otro archivo del proveedor, con "Ver archivo" (FR-010).
 */
export const ImportVoucherInfo = ({ voucher, voucherAlreadyImported, onViewFile }: ImportVoucherInfoProps) => {
  const { t } = useTranslation();
  const tr = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.review.voucher.${key}`, options) as string;
  const label = voucherLabel(voucher, (key, options) => t(key, options) as string);
  const already = voucherAlreadyImported;
  const path = already?.blobPath?.trim();
  if (!label && !already) return null;
  return (
    <>
      {label && (
        <div className="pci-review-voucher">
          <span className="pci-review-voucher__label">{tr('label')}</span>
          <StatusPill tone="info" symbol="▤" icon={<DocumentText16Regular />} data-testid="pci-review-voucher">
            {label}
          </StatusPill>
        </div>
      )}
      {already && (
        <GuideMessageBar intent="warning" layout="multiline" data-testid="pci-review-voucher-imported">
          <MessageBarBody>
            <MessageBarTitle>{tr('alreadyTitle')}</MessageBarTitle>
            {tr('alreadyText', { file: already.fileName || '—', date: importedAtLabel(already.importedAt) || '—' })}
          </MessageBarBody>
          {path && onViewFile && (
            <MessageBarActions>
              <GuideBannerAction onClick={() => onViewFile(path, already.fileName || path)} data-testid="pci-review-voucher-view">
                {tr('viewFile')}
              </GuideBannerAction>
            </MessageBarActions>
          )}
        </GuideMessageBar>
      )}
    </>
  );
};

export interface ImportReviewSummaryProps {
  detected: number;
  selected: number;
  amount: number;
  currencyCode?: string | null;
}

/** Resumen en vivo: "Detectados 9", "Seleccionados 3" e importe seleccionado (15 / Bold / tabular). */
export const ImportReviewSummary = ({ detected, selected, amount, currencyCode }: ImportReviewSummaryProps) => {
  const { t } = useTranslation();
  const tr = (key: string) => t(`providerConsumptionImport.review.${key}`) as string;
  return (
    <div className="pci-review-summary" aria-live="polite" data-testid="pci-review-summary">
      <GuideCountChip label={tr('detected')} count={<span data-testid="pci-review-detected">{detected}</span>} />
      <GuideCountChip label={tr('selected')} count={<span data-testid="pci-review-selected">{selected}</span>} />
      <span className="pci-review-amount">
        <span className="pci-review-amount__label">{tr('selectedAmount')}</span>
        <span className="pci-review-amount__value ichtys-num" data-testid="pci-review-amount">
          {formatImportAmount(amount, currencyCode)}
        </span>
      </span>
    </div>
  );
};

export interface ImportAssignmentBarProps {
  options: AssignmentOptions;
  /** Asignación general (`defaultAssignment`). */
  value?: ImportAssignment;
  onChange: (value: ImportAssignment | undefined) => void;
  selectedCount: number;
  disabled?: boolean;
}

/**
 * Barra "Asignar a seleccionados" (spec 009, US2): protocolo (`ProtocolPicker`) y paciente opcional del protocolo; fija la
 * asignación general (`defaultAssignment`) de todos los seleccionados sin asignación propia.
 */
export const ImportAssignmentBar = ({ options, value, onChange, selectedCount, disabled }: ImportAssignmentBarProps) => {
  const { t } = useTranslation();
  const tr = (key: string, params?: Record<string, unknown>) => t(`providerConsumptionImport.review.assignment.${key}`, params) as string;
  return (
    <section className="pci-review-bar" aria-labelledby="pci-review-assignment-bar-title" data-testid="pci-review-assignment-bar">
      <div className="pci-review-bar__head">
        <h4 id="pci-review-assignment-bar-title" className="pci-review-bar__title">
          {tr('barTitle')}
        </h4>
        <span className="pci-secondary">{tr('barHint', { count: selectedCount })}</span>
        {options.protocolsStatus === 'failed' && (
          <GuideBannerAction onClick={options.retryProtocols} data-testid="pci-review-assignment-retry">
            {tr('retryProtocols')}
          </GuideBannerAction>
        )}
      </div>
      <div className="pci-review-bar__fields">
        <ImportAssignmentFields options={options} value={value} onChange={onChange} disabled={disabled} testIdPrefix="pci-review-assignment" />
        {value && (
          <Button appearance="subtle" icon={<Dismiss16Regular />} onClick={() => onChange(undefined)} disabled={disabled} data-testid="pci-review-assignment-clear">
            {tr('clearGeneral')}
          </Button>
        )}
      </div>
    </section>
  );
};

export interface ImportServiceBarProps {
  /** Elección general (`defaultMedicalService`); sin elección = resolución automática. */
  value?: ItemServiceOverride;
  /** "Recordar para este proveedor" (`rememberServiceMappings`, activa por defecto). */
  remember: boolean;
  onRememberChange: (value: boolean) => void;
  onAssign: () => void;
  onCreate: () => void;
  /** "Volver a automático": quita la elección general. */
  onClear: () => void;
  selectedCount: number;
  disabled?: boolean;
}

/** Texto de una elección de servicio: "Laboratorio central [LC01]", "Nuevo: Neurología (NG)" o "Servicio #40". */
export const serviceChoiceText = (choice: ItemServiceOverride, t: (key: string, params?: Record<string, unknown>) => string): string => {
  if ('medicalServiceId' in choice) return choice.label || t('providerConsumptionImport.review.service.serviceId', { id: choice.medicalServiceId });
  if ('create' in choice) {
    const code = choice.create.code?.trim();
    return code
      ? t('providerConsumptionImport.review.service.createWithCode', { name: choice.create.name, code })
      : t('providerConsumptionImport.review.service.create', { name: choice.create.name });
  }
  return t('providerConsumptionImport.review.service.auto');
};

/**
 * Servicio médico de los seleccionados (spec 009, US6): elección general (`defaultMedicalService`) con "Asignar servicio
 * médico", "Crear servicio nuevo" y "Volver a automático", y la casilla "Recordar para este proveedor" (activa por
 * defecto) que viaja en `rememberServiceMappings`.
 */
export const ImportServiceBar = ({ value, remember, onRememberChange, onAssign, onCreate, onClear, selectedCount, disabled }: ImportServiceBarProps) => {
  const { t } = useTranslation();
  const tr = (key: string, params?: Record<string, unknown>) => t(`providerConsumptionImport.review.service.${key}`, params) as string;
  const chosen = !!value && !('auto' in value);
  return (
    <section className="pci-review-bar" aria-labelledby="pci-review-service-bar-title" data-testid="pci-review-service-bar">
      <div className="pci-review-bar__head">
        <h4 id="pci-review-service-bar-title" className="pci-review-bar__title">
          {tr('barTitle')}
        </h4>
        <span className="pci-secondary">{tr('barHint', { count: selectedCount })}</span>
      </div>
      <div className="pci-review-bar__row">
        <span className="pci-review-bar__value" data-testid="pci-review-service-general">
          {chosen && value ? serviceChoiceText(value, (key, params) => t(key, params) as string) : tr('auto')}
        </span>
        <div className="pci-review-bar__actions">
          <Button appearance="secondary" size="small" onClick={onAssign} disabled={disabled} data-testid="pci-review-service-assign">
            {tr('assign')}
          </Button>
          <Button appearance="secondary" size="small" onClick={onCreate} disabled={disabled} data-testid="pci-review-service-create">
            {tr('createNew')}
          </Button>
          {chosen && (
            <Button appearance="subtle" size="small" icon={<Dismiss16Regular />} onClick={onClear} disabled={disabled} data-testid="pci-review-service-auto">
              {tr('backToAuto')}
            </Button>
          )}
        </div>
      </div>
      <Checkbox
        checked={remember}
        onChange={(_event, data) => onRememberChange(!!data.checked)}
        disabled={disabled}
        label={tr('remember')}
        data-testid="pci-review-service-remember"
      />
      <span className="pci-secondary">{tr('rememberHint')}</span>
    </section>
  );
};

export interface ImportItemErrorsNoticeProps {
  /** `data.itemErrors` del último 400, por `itemId`. */
  itemErrors: ReadonlyMap<string, ImportItemError[]>;
}

/**
 * Aviso "Hay N ítems con errores" (spec 009, FR-014) con "Ver", que lista fila, campo y motivo de cada error aunque la
 * fila esté en otra página. Los errores generales (sin `itemId`) van en el banner del asistente con `errors[]`.
 */
export const ImportItemErrorsNotice = ({ itemErrors }: ImportItemErrorsNoticeProps) => {
  const { t } = useTranslation();
  const tr = (key: string, params?: Record<string, unknown>) => t(`providerConsumptionImport.review.assignment.${key}`, params) as string;
  const [expanded, setExpanded] = useState(false);
  const rows = itemErrorRows(itemErrors);
  if (rows.length === 0) return null;
  const count = itemErrors.size;
  return (
    <GuideMessageBar intent="error" layout="multiline" data-testid="pci-review-item-errors">
      <MessageBarBody>
        <MessageBarTitle>{count === 1 ? tr('itemErrorsOne') : tr('itemErrorsTitle', { count })}</MessageBarTitle>
        {tr('itemErrorsText')}
        {expanded && (
          <ul className="pci-list" id="pci-review-item-errors-list" data-testid="pci-review-item-errors-list">
            {rows.map((row, index) => (
              <li key={`${row.itemId}-${index}`}>
                {tr('itemErrorLine', { row: row.rowNumber ?? '—', field: tr(`errorFields.${row.field}`), message: row.message })}
              </li>
            ))}
          </ul>
        )}
      </MessageBarBody>
      <MessageBarActions>
        <GuideBannerAction
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
          aria-controls={expanded ? 'pci-review-item-errors-list' : undefined}
          data-testid="pci-review-item-errors-toggle"
        >
          {expanded ? tr('hideErrors') : tr('viewErrors')}
        </GuideBannerAction>
      </MessageBarActions>
    </GuideMessageBar>
  );
};
